/**
 * Conditionally show/hide side nav content.
 */
class NavToggle extends HTMLElement {
    /**
     * Indicates if menu is currently minimized.
     * Nav will always start out maximized.
     * @type {boolean}
     */
    #minimized = false;

    /**
     * Name of the key for storing the minimized state in localStorage.
     * @type {string}
     */
    #keyName = 'navToggle_minimized';

    /**
     * Screen width for mobile menu.
     * @type {number}
     */
    #mobileBreakpointWidth = 1024;

    constructor() {
        super();

        // use localStorage to store state of menu and restore on load
        this.#minimized = localStorage.getItem(this.#keyName) === 'true';

        this.querySelectorAll('[data-nav-toggle-target]').forEach((toggle) => {
            toggle.addEventListener('click', this);
        });

        // Close active nav on mobile when a nav link is clicked
        const nav = this.querySelector('nav');
        if (nav) {
            if (this.#minimized && window.innerWidth > this.#mobileBreakpointWidth) {
                nav.classList.add('minimized');
            }

            nav.addEventListener('click', (event) => {
                if (window.innerWidth > this.#mobileBreakpointWidth) {
                    return;
                }

                const target = event.target;
                if (!(target instanceof Element)) {
                    return;
                }

                const link = target.closest('a:not([href="#"])');
                if (!link) {
                    return;
                }

                const activeNav = this.querySelector('.active');
                if (activeNav) {
                    activeNav.classList.remove('active');
                }
            });
        }
    }

    /**
     * Conditionally show/hide content.
     * @param {Event} event Click event.
     */
    handleEvent(event) {
        /** @type {HTMLElement|null} */
        const targetNode = event.target instanceof Element ? event.target.closest('[data-nav-toggle-target]') : null;
        const target = targetNode?.dataset.navToggleTarget ? document.getElementById(targetNode.dataset.navToggleTarget) : null;
        if (!target) {
            return;
        }

        if (window.innerWidth <= this.#mobileBreakpointWidth) {
            // reset minimized toggle since it's not applicable on mobile
            this.#minimized = false;
            localStorage.removeItem(this.#keyName);

            target.classList.remove('minimized');
            target.classList.toggle('active');
        } else {
            this.#minimized = !this.#minimized;
            if (this.#minimized) {
                localStorage.setItem(this.#keyName, 'true');
            } else {
                localStorage.removeItem(this.#keyName);
            }

            target.classList.remove('active');
            target.classList.toggle('minimized');
        }
    }
}

// Define the new web component
if ('customElements' in window) {
    customElements.define('nilla-nav-toggle', NavToggle);
}

export default NavToggle;
